/**
 * Pano listesi — toplayıcının OKUDUĞU yapılandırmanın görünen yüzü.
 *
 * Bu ekrandaki her alan `loadPanels()`'in okuduğu bir kolona karşılık gelir:
 * adres, port, yoklama aralığı, yoklama açık mı. Yani buradaki bir hata
 * doğrudan "veri gelmiyor" demektir — eski sistemdeki `0.0.0.0` panosu bu
 * ekran olsaydı ilk bakışta görünürdü.
 */
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useCallback } from 'react';

import {
  AramaKutusu,
  EklemeBaglantisi,
  FiltreDugmesi,
  FiltreSatiri,
  KapsamNotu,
  ListeDurumu,
  YapilandirmaSekmeleri,
  useAramaYazici,
} from './parts.tsx';
import { Badge } from '../design/Status.tsx';
import { Yetki } from '../auth/Yetki.tsx';
import { Card, CardBody } from '../design/Card.tsx';
import { PageBody, PageHeader } from '../shell/AppShell.tsx';
import { aralikMetni, baglantiEtiketi, erisimAdresi, panoDurumu } from './durum.ts';
import { usePanolar, type PanoSatiri } from './api.ts';

export function PanoListesi(): React.JSX.Element {
  const { q, mod, yoklama } = useSearch({ from: '/korumali/panolar' });
  const navigate = useNavigate();

  const git = useCallback(
    (yeni: {
      q?: string | undefined;
      mod?: 'public' | 'vpn' | undefined;
      yoklama?: 'acik' | 'kapali' | undefined;
    }) => {
      void navigate({
        to: '/panolar',
        search: {
          ...(yeni.q === undefined ? {} : { q: yeni.q }),
          ...(yeni.mod === undefined ? {} : { mod: yeni.mod }),
          ...(yeni.yoklama === undefined ? {} : { yoklama: yeni.yoklama }),
        },
        replace: true,
      });
    },
    [navigate],
  );
  const aramaDegisti = useAramaYazici(
    useCallback(
      (deger: string | undefined) => git({ q: deger, mod, yoklama }),
      [git, mod, yoklama],
    ),
  );
  const temizle = useCallback(() => git({}), [git]);

  const sorgu = usePanolar({ q, mod, yoklama });

  return (
    <>
      <PageHeader
        title="Panolar"
        description="PLC bağlantı ayarları ve yoklama durumu"
        action={
          <EklemeBaglantisi gerekli="add_panel" to="/panolar/yeni" etiket="Pano ekle" vurgulu />
        }
      />
      <YapilandirmaSekmeleri />
      <KapsamNotu />
      <AramaKutusu deger={q ?? ''} degisti={aramaDegisti} ipucu="Seri numarası, saha adı veya IP" />

      {/*
       * VPN geçişi pano pano yapılıyor [K-005]; "hangileri hâlâ genel ağda?"
       * sorusu geçiş boyunca her gün sorulacak.
       */}
      <FiltreSatiri>
        <FiltreDugmesi
          etkin={mod === 'public'}
          onClick={() => git({ q, yoklama, mod: mod === 'public' ? undefined : 'public' })}
        >
          Genel ağ
        </FiltreDugmesi>
        <FiltreDugmesi
          etkin={mod === 'vpn'}
          onClick={() => git({ q, yoklama, mod: mod === 'vpn' ? undefined : 'vpn' })}
        >
          VPN
        </FiltreDugmesi>
        <FiltreDugmesi
          etkin={yoklama === 'kapali'}
          onClick={() => git({ q, mod, yoklama: yoklama === 'kapali' ? undefined : 'kapali' })}
        >
          Yoklaması kapalı
        </FiltreDugmesi>
      </FiltreSatiri>

      <PageBody>
        <ListeDurumu
          sorgu={sorgu}
          filtreVar={q !== undefined || mod !== undefined || yoklama !== undefined}
          filtreyiTemizle={temizle}
          bosBaslik="Henüz pano yok"
          bosAciklama="Bir sahadan veri toplanabilmesi için önce panosu tanımlanmalı."
          bosEylem={
            <EklemeBaglantisi
              gerekli="add_panel"
              to="/panolar/yeni"
              etiket="İlk panoyu ekle"
              vurgulu
            />
          }
        >
          {(panolar) => (
            <ul className="flex flex-col gap-3">
              {panolar.map((pano) => (
                <li key={pano.id}>
                  <PanoKarti pano={pano} sahaAdi={pano.site_name} />
                </li>
              ))}
            </ul>
          )}
        </ListeDurumu>
      </PageBody>
    </>
  );
}

/** Saha detayında da kullanılıyor — orada saha adı zaten başlıkta, o yüzden opsiyonel. */
export function PanoKarti({
  pano,
  sahaAdi,
}: {
  readonly pano: Omit<PanoSatiri, 'site_id' | 'site_name' | 'city'>;
  readonly sahaAdi?: string | undefined;
}): React.JSX.Element {
  const durum = panoDurumu(pano);
  const adres = erisimAdresi(pano);

  return (
    <Card>
      <CardBody>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="truncate text-base font-semibold text-ink">{pano.serial_number}</p>
            {sahaAdi !== undefined && (
              <p className="mt-0.5 truncate text-sm text-ink-muted">{sahaAdi}</p>
            )}
          </div>
          <div className="flex shrink-0 flex-col items-end gap-2">
            <Badge tone={durum.tone}>{durum.metin}</Badge>
            {/* Düzenleme yolu KARTTA: kullanıcı listeden çıkmadan düzeltebilsin. */}
            <Yetki gerekli="edit_panel">
              <Link
                to="/panolar/$panoId/duzenle"
                params={{ panoId: String(pano.id) }}
                className="text-sm font-medium text-brand-700"
              >
                Düzenle
              </Link>
            </Yetki>
          </div>
        </div>

        <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-sm text-ink-muted sm:grid-cols-3">
          <div className="flex min-w-0 gap-1">
            <dt>Erişim:</dt>
            <dd className="truncate font-medium text-ink">
              {/*
               * Adres yoksa "—" değil, AÇIK BİR EKSİK yazıyoruz. Şema
               * `ck_panel_connection` ile bunu zaten engelliyor; yine de
               * görünürse veri bozulmuş demektir ve saklanmamalı.
               */}
              {adres ?? 'adres tanımsız'}
            </dd>
          </div>
          <div className="flex gap-1">
            <dt>Tür:</dt>
            <dd className="font-medium text-ink">{baglantiEtiketi(pano.connection_mode)}</dd>
          </div>
          <div className="flex min-w-0 gap-1">
            <dt>PLC:</dt>
            <dd className="truncate font-medium text-ink">
              {pano.plc_host}:{pano.plc_port}
            </dd>
          </div>
          <div className="flex gap-1">
            <dt>Aralık:</dt>
            <dd className="font-medium text-ink">{aralikMetni(pano.poll_interval_seconds)}</dd>
          </div>
          {pano.plc_brand !== null && (
            <div className="flex min-w-0 gap-1">
              <dt>Profil:</dt>
              <dd className="truncate font-medium text-ink">{pano.plc_brand}</dd>
            </div>
          )}
        </dl>
      </CardBody>
    </Card>
  );
}
